import { useEffect, useMemo, useState } from 'react';
import { useStore } from '../store';
import {
  deleteMyPayload,
  listMyPayloads,
  listPublicPayloads,
  type PayloadEntry,
} from '../api-payloads';

function rowId(): string {
  return Math.random().toString(36).slice(2, 10);
}

function StatusPill({ status }: { status: PayloadEntry['status'] }) {
  return <span className={`payload-pill ${status}`}>{status}</span>;
}

type SortMode = 'recent' | 'alpha';

const TAGS_STORAGE_KEY = 'xsscool:show-tags';

function loadShowTags(): boolean {
  if (typeof localStorage === 'undefined') return false;
  return localStorage.getItem(TAGS_STORAGE_KEY) === '1';
}
function persistShowTags(show: boolean): void {
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(TAGS_STORAGE_KEY, show ? '1' : '0');
    }
  } catch {
    /* ignore */
  }
}

function tagCounts(items: PayloadEntry[]): { tag: string; count: number }[] {
  const counts = new Map<string, number>();
  for (const p of items) {
    for (const t of p.tags) counts.set(t, (counts.get(t) ?? 0) + 1);
  }
  return [...counts.entries()]
    .map(([tag, count]) => ({ tag, count }))
    .sort((a, b) => b.count - a.count || a.tag.localeCompare(b.tag));
}

export function PayloadLibrary({ onApplied }: { onApplied?: () => void } = {}) {
  const applyPreset = useStore((s) => s.applyPreset);
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [sortMode, setSortMode] = useState<SortMode>('recent');
  const [showTags, setShowTagsState] = useState<boolean>(loadShowTags);
  const setShowTags = (v: boolean) => {
    persistShowTags(v);
    setShowTagsState(v);
    // If hiding the cloud while a tag filter is active, clear the filter so
    // the user isn't left with a hidden-but-applied filter they can't undo.
    if (!v) setActiveTag(null);
  };
  const [list, setList] = useState<PayloadEntry[]>([]);
  const [mine, setMine] = useState<PayloadEntry[]>([]);
  const [showMine, setShowMine] = useState(false);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    const [pub, own] = await Promise.all([listPublicPayloads(), listMyPayloads()]);
    setList(pub);
    setMine(own);
    setLoading(false);
  };

  useEffect(() => {
    void reload();
    const onChanged = () => {
      void reload().then(() => setShowMine(true));
    };
    window.addEventListener('xsscool:payloads-changed', onChanged);
    return () => window.removeEventListener('xsscool:payloads-changed', onChanged);
  }, []);

  const tags = useMemo(() => tagCounts(list), [list]);

  const filtered = useMemo(() => {
    let out = list;
    if (activeTag) out = out.filter((p) => p.tags.includes(activeTag));
    if (search) {
      const q = search.toLowerCase();
      out = out.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.description.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q)),
      );
    }
    if (sortMode === 'alpha') {
      out = [...out].sort((a, b) => a.title.localeCompare(b.title));
    } else {
      out = [...out].sort((a, b) => b.createdAt - a.createdAt);
    }
    return out;
  }, [list, activeTag, search, sortMode]);

  const toggleTag = (t: string) => setActiveTag((cur) => (cur === t ? null : t));

  const apply = (p: PayloadEntry) => {
    applyPreset(
      {
        ...p.state,
        headers: p.state.headers.map((h) => ({ id: rowId(), name: h.name, value: h.value })),
      },
      { label: p.title },
    );
    onApplied?.();
  };

  const handleDelete = async (id: string) => {
    await deleteMyPayload(id);
    await reload();
  };

  return (
    <>
      <div className="payload-actions">
        {mine.length > 0 && (
          <button
            className="payload-mine-toggle"
            onClick={() => setShowMine((v) => !v)}
            type="button"
          >
            {showMine ? 'Hide' : 'Show'} my submissions ({mine.length})
          </button>
        )}
      </div>
      <input
        className="payload-search"
        placeholder="Search payloads…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />
      {tags.length > 0 && showTags && (
        <div className="payload-tagcloud" role="group" aria-label="Filter by tag">
          <button
            type="button"
            className={`payload-tagchip${activeTag ? '' : ' active'}`}
            onClick={() => setActiveTag(null)}
            title="Show all payloads"
          >
            all
          </button>
          {tags.map(({ tag, count }) => (
            <button
              key={tag}
              type="button"
              className={`payload-tagchip${activeTag === tag ? ' active' : ''}`}
              onClick={() => toggleTag(tag)}
              title={`Filter by #${tag} (${count})`}
            >
              #{tag} <span className="payload-tagchip-count">{count}</span>
            </button>
          ))}
        </div>
      )}
      <div className="payload-controls">
        <label className="payload-sort">
          <span>Sort</span>
          <select value={sortMode} onChange={(e) => setSortMode(e.target.value as SortMode)}>
            <option value="recent">Recent</option>
            <option value="alpha">A→Z</option>
          </select>
        </label>
        {tags.length > 0 && (
          <button
            type="button"
            className={`payload-tags-toggle${showTags ? ' active' : ''}`}
            onClick={() => setShowTags(!showTags)}
            aria-pressed={showTags}
            title={showTags ? 'Hide tag filter' : 'Show tag filter'}
          >
            {showTags ? 'Hide tags' : `Tags (${tags.length})`}
          </button>
        )}
        <span className="payload-result-count">
          {filtered.length} / {list.length}
        </span>
      </div>
      {loading ? (
        <p className="hint">Loading…</p>
      ) : (
        <ul className="payload-list">
          {filtered.length === 0 && <li className="hint">No payloads match.</li>}
          {filtered.map((p) => (
            <li key={p.id}>
              <button onClick={() => apply(p)} title={p.description}>
                <strong>{p.title}</strong>
                <span>{p.description}</span>
                {(showTags && (p.tags.length > 0 || p.alias)) && (
                  <span className="payload-meta">
                    {p.alias && <span className="payload-alias">{p.alias}</span>}
                    {p.tags.map((t) => (
                      <span
                        key={t}
                        className={`payload-tag${activeTag === t ? ' active' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleTag(t);
                        }}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleTag(t);
                          }
                        }}
                        title={`Filter by #${t}`}
                      >
                        #{t}
                      </span>
                    ))}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}

      {showMine && mine.length > 0 && (
        <>
          <h2>My submissions</h2>
          <ul className="payload-list mine">
            {mine.map((p) => (
              <li key={p.id}>
                <button onClick={() => apply(p)}>
                  <strong>{p.title}</strong>
                  <span>{p.description}</span>
                  <span className="payload-meta">
                    <StatusPill status={p.status} />
                    {p.tags.map((t) => (
                      <span key={t} className="payload-tag">
                        #{t}
                      </span>
                    ))}
                  </span>
                </button>
                {p.status === 'pending' && (
                  <button
                    className="payload-delete"
                    onClick={() => void handleDelete(p.id)}
                    aria-label="Delete pending submission"
                    title="Delete pending submission"
                    type="button"
                  >
                    ×
                  </button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

    </>
  );
}
